/**
 * Unified AI Assessment Route
 *
 * Displays the unified AI assessment tab that combines:
 * - Triage (FNOL-based quick assessment)
 * - Full Assessment (document-based coverage analysis)
 * - Fraud Watch (red flag detection)
 *
 * Feature flagged behind 'enable-ai-unified-assessment'.
 *
 * Supports deep-linking to specific sub-tabs via the `tab` search param.
 */

import { PageController } from '@client/components/_core/layout/PageController';
import { AwaitingProductGate } from '@client/components/claims/product-binding/ClaimAwaitingProduct';
import { UnifiedAssessmentTab } from '@client/components/claims/unified-assessment';
import { useUser } from '@client/hooks/business/user/useUser';
import { AI_ASSESSMENT_FEATURE_FLAG, AI_ASSESSMENT_SUB_TABS } from '@shared/constants/ai-assessment';
import { hasClaimUpdateAccess } from '@shared/permissions/claim';
import { createFileRoute } from '@tanstack/react-router';
import { z } from 'zod';

import { claimFormStore } from '../store';

const searchParamsSchema = z.object({
    tab: z.enum(AI_ASSESSMENT_SUB_TABS).optional().catch(undefined),
});

export const Route = createFileRoute('/console/$orgId/claims/details/$id/ai-assessment')({
    staticData: {
        featureFlag: AI_ASSESSMENT_FEATURE_FLAG,
        metadata: {
            title: 'AI assessment',
        },
    },
    validateSearch: searchParamsSchema,
    component: UnifiedAssessmentRoute,
});

function UnifiedAssessmentRoute() {
    const claim = claimFormStore.use.formData();
    const user = useUser();
    const isReadonly = claimFormStore.use.isReadonly();
    // Shared with the server's hasClaimWriteAccess + CUR-725 closed-claim gates on the
    // run/cancel endpoints — a closed claim renders the tab read-only.
    const canManageAssessments = !!claim && !isReadonly && hasClaimUpdateAccess(user, claim);

    return (
        <PageController route={Route}>
            {claim && (
                <AwaitingProductGate
                    claim={claim}
                    title="The assessment needs the product"
                    description="The assessment reads the product's policy wording and limits. Choose it and this tab opens."
                >
                    <UnifiedAssessmentTab
                        claimId={claim.id}
                        canManageAssessments={canManageAssessments}
                        route={Route}
                    />
                </AwaitingProductGate>
            )}
        </PageController>
    );
}
